import {
  INotificationBaseRelatedToShow,
  IPayloadUpdateRefunds,
  IRefund,
  NotificationType,
  RefundStatus,
} from "@evennt-tech/api-v2-core";
import { IGetManyRefundsBodyReq } from "@evennt-tech/api-v2-external";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";
import groupBy from "lodash.groupby";
import { shallow } from "zustand/shallow";

type State = {
  refundsByOrder: Map<string, IRefund[]>;
  isLoading: boolean;
};

type Action = {
  fetch: (params: IGetManyRefundsBodyReq) => Promise<void>;
  fetchOne: (
    params: Pick<IGetManyRefundsBodyReq, "showId" | "supplierCompanyId"> & {
      orderId: string;
    }
  ) => Promise<void>;
  autoCatch: (
    params: Pick<IGetManyRefundsBodyReq, "supplierCompanyId" | "showId">
  ) => () => void;
  reset: () => void;
};

type Store = Action & State;

export const refundStore = create(
  devtools<Store>(
    (set, get) => ({
      isLoading: false,
      refundsByOrder: new Map(),
      fetch: async ({ showId, supplierCompanyId, orderIds, statuses }) => {
        if (!orderIds.length) {
          return;
        }
        const { refundsByOrder } = get();
        try {
          set({ isLoading: true });
          const refunds = await apiV2.refund.getMany({
            showId,
            orderIds,
            ...(supplierCompanyId && { supplierCompanyId }),
            ...(statuses?.length && { statuses }),
          });

          const groupedRefunds = groupBy(refunds, "orderId");
          const newRefundsByOrder = new Map(refundsByOrder);

          for (const [orderId, refunds] of Object.entries(groupedRefunds)) {
            newRefundsByOrder.set(orderId, refunds);
          }

          set({ refundsByOrder: newRefundsByOrder });
        } catch (e) {
          console.error("Error occurred fetching refunds: ", e);
        } finally {
          set({ isLoading: false });
        }
      },

      fetchOne: async ({ orderId, supplierCompanyId, showId }) => {
        try {
          set({ isLoading: true });

          const statuses: RefundStatus[] = [
            RefundStatus.Processed,
            RefundStatus.Processing,
          ];
          if (supplierCompanyId) {
            statuses.push(RefundStatus.Failed);
            statuses.push(RefundStatus.InReview);
          }

          const refunds = await apiV2.refund.getMany({
            showId,
            orderIds: [orderId],
            ...(supplierCompanyId && { supplierCompanyId }),
            statuses,
          });

          const newRefundsByOrder = new Map(get().refundsByOrder);

          newRefundsByOrder.set(orderId, refunds);

          set({ refundsByOrder: newRefundsByOrder });
        } catch (e) {
          console.error("Error occurred fetching refunds: ", e);
        } finally {
          set({ isLoading: false });
        }
      },
      autoCatch: ({ showId, supplierCompanyId }) => {
        const { fetchOne: fetchRefunds } = get();
        apiV2.notification.subscribe<
          INotificationBaseRelatedToShow<IPayloadUpdateRefunds>
        >({
          showId,
          key: NotificationType.UPDATE_REFUNDS,
          callback: async ({ payload }) => {
            if (!payload) {
              return;
            }

            await fetchRefunds({
              showId,
              orderId: payload.orderId,
              ...(supplierCompanyId && { supplierCompanyId }),
            });
          },
        });

        return () =>
          apiV2.notification.unsubscribe({
            key: NotificationType.UPDATE_REFUNDS,
            showId,
          });
      },
      reset: () => {
        set({ isLoading: false, refundsByOrder: new Map() });
      },
    }),
    { name: "refund-store" }
  )
);

const useRefundStore = <Return = Partial<Store>>(
  selector: (store: Store) => Return
) => refundStore(selector, shallow);

export const useRefundStoreActions = () =>
  useRefundStore(({ fetch, autoCatch, reset }) => ({
    fetchRefunds: fetch,
    autoCatchRefunds: autoCatch,
    resetRefunds: reset,
  }));
export const useOrderRefunds = (
  orderId: string
): { isLoading: boolean; refunds: IRefund[] } =>
  useRefundStore(({ refundsByOrder, isLoading }) => ({
    isLoading,
    refunds: refundsByOrder.get(orderId) ?? [],
  }));
export const useRefunds = () =>
  useRefundStore(
    ({ refundsByOrder, isLoading }) => ({ refundsByOrder, isLoading } as const)
  );
